import { Link } from "react-router-dom";
import { PageLayout } from "@/components/PageLayout";
import { CalculatorCard } from "@/components/CalculatorCard";
import { Calculator, Percent, Divide, Square, Shapes, Circle, Triangle, Activity, PieChart, Binary, Table, Search } from "lucide-react";
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import logoLight from "@/assets/logo-light_t.png";
import logoDark from "@/assets/logo-dark_t.png";
import { useTranslation } from "react-i18next";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema } from "@/utils/structuredData";

const simpleCalculators = {
  arithmeticAlgebra: [
    {
      title: "Basic Arithmetic",
      description: "Addition, subtraction, multiplication, division",
      icon: Calculator,
      href: "/calculator/basic-arithmetic",
      category: "math" as const,
    },
    {
      title: "Percentage",
      description: "Increase, decrease, discounts",
      icon: Percent,
      href: "/calculator/percentage",
      category: "math" as const,
    },
    {
      title: "Fraction Calculator",
      description: "Simplify, add, subtract, multiply, divide",
      icon: Divide,
      href: "/calculator/fraction",
      category: "math" as const,
    },
    {
      title: "Exponent & Root",
      description: "Powers, square roots, cube roots",
      icon: Square,
      href: "/calculator/exponent-root",
      category: "math" as const,
    },
    {
      title: "Linear Equation Solver",
      description: "Ax + B = C",
      icon: Activity,
      href: "/calculator/linear-equation",
      category: "math" as const,
    },
    {
      title: "Quadratic Equation",
      description: "ax² + bx + c = 0",
      icon: Activity,
      href: "/calculator/quadratic-equation",
      category: "math" as const,
    },
    {
      title: "Factor Calculator",
      description: "Prime factorization, GCF, LCM",
      icon: Binary,
      href: "/calculator/factor",
      category: "math" as const,
    },
    {
      title: "Simplify Expressions",
      description: "Algebraic expression simplifier",
      icon: Calculator,
      href: "/calculator/simplify-expressions",
      category: "math" as const,
    },
  ],
  geometry: [
    {
      title: "Area & Perimeter",
      description: "Squares, rectangles, circles, triangles",
      icon: Shapes,
      href: "/calculator/area-perimeter",
      category: "math" as const,
    },
    {
      title: "Volume Calculator",
      description: "Cubes, spheres, cylinders, cones",
      icon: Square,
      href: "/calculator/volume",
      category: "math" as const,
    },
    {
      title: "Pythagorean Theorem",
      description: "a² + b² = c²",
      icon: Triangle,
      href: "/calculator/pythagorean",
      category: "math" as const,
    },
    {
      title: "Angle Calculator",
      description: "Interior, exterior, supplementary",
      icon: Activity,
      href: "/calculator/angle",
      category: "math" as const,
    },
    {
      title: "Circle Geometry",
      description: "Arc length, sector area",
      icon: Circle,
      href: "/calculator/circle-geometry",
      category: "math" as const,
    },
  ],
  trigonometry: [
    {
      title: "Trig Functions",
      description: "sin, cos, tan calculator",
      icon: Activity,
      href: "/calculator/trig-functions",
      category: "math" as const,
    },
    {
      title: "Right Triangle Solver",
      description: "Solve for sides and angles",
      icon: Triangle,
      href: "/calculator/right-triangle",
      category: "math" as const,
    },
    {
      title: "Unit Circle",
      description: "Visualize angles and coordinates",
      icon: Circle,
      href: "/calculator/unit-circle",
      category: "math" as const,
    },
    {
      title: "Law of Sines & Cosines",
      description: "General triangle solver",
      icon: Triangle,
      href: "/calculator/law-sines-cosines",
      category: "math" as const,
    },
  ],
};

const advancedCalculators = {
  algebraLinearAlgebra: [
    {
      title: "Matrix Calculator",
      description: "Addition, multiplication, inverse, determinant",
      icon: Table,
      href: "/calculator/matrix",
      category: "math" as const,
    },
    {
      title: "System of Equations",
      description: "2×2, 3×3 systems solver",
      icon: Calculator,
      href: "/calculator/system-equations",
      category: "math" as const,
    },
    {
      title: "Eigenvalue & Eigenvector",
      description: "Linear algebra calculations",
      icon: Activity,
      href: "/calculator/eigenvalue",
      category: "math" as const,
    },
    {
      title: "Polynomial Solver",
      description: "Solve and graph polynomials",
      icon: Activity,
      href: "/calculator/polynomial",
      category: "math" as const,
    },
  ],
  calculus: [
    {
      title: "Derivative Calculator",
      description: "Step-by-step differentiation",
      icon: Activity,
      href: "/calculator/derivative",
      category: "math" as const,
    },
    {
      title: "Integral Calculator",
      description: "Definite and indefinite integrals",
      icon: Activity,
      href: "/calculator/integral",
      category: "math" as const,
    },
    {
      title: "Limit Calculator",
      description: "Calculate limits",
      icon: Calculator,
      href: "/calculator/limit",
      category: "math" as const,
    },
    {
      title: "Taylor Series",
      description: "Series expansion tool",
      icon: Activity,
      href: "/calculator/taylor-series",
      category: "math" as const,
    },
    {
      title: "Gradient & Partial Derivatives",
      description: "Multivariable calculus",
      icon: Activity,
      href: "/calculator/gradient",
      category: "math" as const,
    },
  ],
  statistics: [
    {
      title: "Mean, Median, Mode",
      description: "Central tendency measures",
      icon: PieChart,
      href: "/calculator/mean-median-mode",
      category: "math" as const,
    },
    {
      title: "Standard Deviation",
      description: "Variance and standard deviation",
      icon: PieChart,
      href: "/calculator/standard-deviation",
      category: "math" as const,
    },
    {
      title: "Z-score Calculator",
      description: "Standard score calculation",
      icon: Calculator,
      href: "/calculator/z-score",
      category: "math" as const,
    },
    {
      title: "Probability Calculator",
      description: "Independent, dependent events",
      icon: Percent,
      href: "/calculator/probability",
      category: "math" as const,
    },
    {
      title: "Distribution Calculator",
      description: "Binomial & normal distribution",
      icon: PieChart,
      href: "/calculator/distribution",
      category: "math" as const,
    },
    {
      title: "Regression Analysis",
      description: "Linear, polynomial regression",
      icon: Activity,
      href: "/calculator/regression",
      category: "math" as const,
    },
  ],
  discreteMath: [
    {
      title: "Permutation & Combination",
      description: "Calculate arrangements",
      icon: Binary,
      href: "/calculator/permutation-combination",
      category: "math" as const,
    },
    {
      title: "Set Theory",
      description: "Union, intersection, complement",
      icon: Circle,
      href: "/calculator/set-theory",
      category: "math" as const,
    },
    {
      title: "Truth Table Generator",
      description: "Logic table builder",
      icon: Table,
      href: "/calculator/truth-table",
      category: "math" as const,
    },
    {
      title: "Logic Simplifier",
      description: "Boolean expression simplification",
      icon: Binary,
      href: "/calculator/logic-simplifier",
      category: "math" as const,
    },
  ],
};

export default function Math() {
  const { t } = useTranslation();
  const [level, setLevel] = useState<"simple" | "advanced">("simple");

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: t('categories.pages.math.title'), url: "https://calclab.app/math" }
    ], lang)
  ]);

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">{t('categories.pages.math.title')}</h1>
          <p className="text-muted-foreground">
            {t('categories.pages.math.description')}
          </p>
        </div>

        <div className="flex justify-center gap-2 mb-6">
          <Button
            variant={level === "simple" ? "default" : "outline"}
            onClick={() => setLevel("simple")}
          >
            {t('categories.pages.math.simple')}
          </Button>
          <Button
            variant={level === "advanced" ? "default" : "outline"}
            onClick={() => setLevel("advanced")}
          >
            {t('categories.pages.math.advanced')}
          </Button>
        </div>

        {level === "simple" ? (
          <Tabs defaultValue="arithmeticAlgebra" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-8">
              <TabsTrigger value="arithmeticAlgebra">Arithmetic & Algebra</TabsTrigger>
              <TabsTrigger value="geometry">Geometry</TabsTrigger>
              <TabsTrigger value="trigonometry">Trigonometry</TabsTrigger>
            </TabsList>

            <TabsContent value="arithmeticAlgebra">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {simpleCalculators.arithmeticAlgebra.map((calc) => (
                  <CalculatorCard key={calc.title} {...calc} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="geometry">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {simpleCalculators.geometry.map((calc) => (
                  <CalculatorCard key={calc.title} {...calc} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="trigonometry">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {simpleCalculators.trigonometry.map((calc) => (
                  <CalculatorCard key={calc.title} {...calc} />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        ) : (
          <Tabs defaultValue="algebraLinearAlgebra" className="w-full">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 mb-8">
              <TabsTrigger value="algebraLinearAlgebra">Algebra & Linear Algebra</TabsTrigger>
              <TabsTrigger value="calculus">Calculus</TabsTrigger>
              <TabsTrigger value="statistics">Statistics & Probability</TabsTrigger>
              <TabsTrigger value="discreteMath">Discrete Math</TabsTrigger>
            </TabsList>

            <TabsContent value="algebraLinearAlgebra">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {advancedCalculators.algebraLinearAlgebra.map((calc) => (
                  <CalculatorCard key={calc.title} {...calc} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="calculus">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {advancedCalculators.calculus.map((calc) => (
                  <CalculatorCard key={calc.title} {...calc} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="statistics">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {advancedCalculators.statistics.map((calc) => (
                  <CalculatorCard key={calc.title} {...calc} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="discreteMath">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {advancedCalculators.discreteMath.map((calc) => (
                  <CalculatorCard key={calc.title} {...calc} />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </PageLayout>
  );
}
